/** Privacy / Gatekeeper Settings — /privacy
 *
 * Two panels:
 * 1. Direct DM Privacy — toggle ``celeb_gatekept`` + manage staff
 *    contacts that will be surfaced to blocked senders.
 * 2. Whitelist — explicit user IDs allowed to DM directly despite the
 *    gate. (Simple text-input adder for MVP; a Directory-picker can
 *    come later.)
 */
import { useEffect, useState } from "react";
import { Lock, Unlock, Trash2, Plus, User as UserIcon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export default function PrivacyGatekeeper() {
  const [data, setData] = useState(null);
  const [contactsDraft, setContactsDraft] = useState([]);
  const [wlInput, setWlInput] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/users/me/gatekeeper");
    setData(data);
    setContactsDraft(data.staff_contacts || []);
  };
  useEffect(() => { void load(); }, []);

  if (!data) {
    return <div className="p-10 text-white/50 text-sm">Loading privacy settings…</div>;
  }

  const flipFlag = async (next) => {
    setSaving(true);
    try {
      const { data: fresh } = await api.patch("/users/me/gatekeeper", {
        celeb_gatekept: next,
      });
      setData(fresh);
      setContactsDraft(fresh.staff_contacts || []);
      toast.success(next
        ? "Direct DMs are now GATED. Senders will see your staff contacts."
        : "Direct DMs are OPEN.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const saveContacts = async () => {
    setSaving(true);
    try {
      const clean = contactsDraft
        .filter((c) => (c.name || "").trim() && (c.role || "").trim())
        .map((c) => ({
          name:  (c.name || "").trim(),
          role:  (c.role || "").trim(),
          phone: (c.phone || "").trim(),
          email: (c.email || "").trim(),
        }));
      const { data: fresh } = await api.patch("/users/me/gatekeeper", {
        staff_contacts: clean,
      });
      setData(fresh);
      setContactsDraft(fresh.staff_contacts || []);
      toast.success("Staff contacts saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const addContactRow = () => {
    setContactsDraft([...contactsDraft, {
      name: "", role: "", phone: "", email: "",
    }]);
  };

  const patchRow = (idx, key, val) => {
    setContactsDraft(contactsDraft.map((c, i) =>
      i === idx ? { ...c, [key]: val } : c));
  };

  const dropRow = (idx) => {
    setContactsDraft(contactsDraft.filter((_, i) => i !== idx));
  };

  const addWhitelist = async () => {
    const uid = wlInput.trim();
    if (!uid) return;
    try {
      const { data: fresh } = await api.post("/users/me/dm-whitelist", { user_id: uid });
      setData(fresh);
      setWlInput("");
      toast.success("Added to whitelist.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeWhitelist = async (uid) => {
    try {
      const { data: fresh } = await api.delete(`/users/me/dm-whitelist/${encodeURIComponent(uid)}`);
      setData(fresh);
      toast.success("Removed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const gated = !!data.celeb_gatekept;

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10" data-testid="privacy-page">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Direct Message Privacy</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">
        Celebrity Gatekeeper
      </h1>
      <p className="mt-3 text-sm text-white/55 max-w-2xl">
        When gated, strangers can no longer DM you directly. They will
        be shown your <span className="text-white">staff contact rows</span>
        (manager, secretary, PR) and routed via those channels or into
        the Production House shared mailbox instead. You can still
        whitelist specific individuals for direct access.
      </p>

      {/* ─────────────────────── Toggle ─────────────────────── */}
      <div className="mt-8 cs-card rounded-2xl p-6" data-testid="gatekeeper-toggle-panel">
        <div className="flex items-center gap-4">
          <div className={"w-12 h-12 rounded-full flex items-center justify-center "
                          + (gated ? "bg-amber-500/15" : "bg-emerald-500/15")}>
            {gated
              ? <Lock size={22} className="text-amber-400"/>
              : <Unlock size={22} className="text-emerald-400"/>}
          </div>
          <div className="flex-1">
            <div className="font-display text-xl">{gated ? "Gated" : "Open"}</div>
            <div className="text-xs text-white/55">
              {gated
                ? "Strangers must contact your staff first."
                : "Anyone can send you a direct message."}
            </div>
          </div>
          <Switch
            checked={gated}
            onCheckedChange={flipFlag}
            disabled={saving}
            data-testid="gatekeeper-toggle"
          />
        </div>
      </div>

      {/* ─────────────────────── Staff Contacts ─────────────────────── */}
      <div className="mt-8 cs-card rounded-2xl p-6" data-testid="staff-contacts-panel">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">Staff Contacts</div>
            <div className="font-display text-xl">Manager · Secretary · PR</div>
          </div>
          <Button onClick={addContactRow} variant="ghost"
                  className="text-white/70 hover:text-white"
                  data-testid="add-staff-contact-btn">
            <Plus size={14}/> Add row
          </Button>
        </div>

        <div className="space-y-3">
          {contactsDraft.length === 0 && (
            <div className="text-xs text-white/45 border border-dashed border-white/10 rounded-lg p-6 text-center">
              No staff contacts yet. Click <em>Add row</em> to attach a
              Manager / Secretary / PR intake channel.
            </div>
          )}
          {contactsDraft.map((c, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end p-3 border border-white/10 rounded-lg"
                 data-testid={`staff-contact-row-${i}`}>
              <RowField label="Name">
                <Input value={c.name || ""} placeholder="Preeti Mehra"
                       onChange={(e) => patchRow(i, "name", e.target.value)}
                       className="bg-black/40 border-white/15"
                       data-testid={`staff-name-${i}`}/>
              </RowField>
              <RowField label="Role">
                <Input value={c.role || ""} placeholder="Manager"
                       onChange={(e) => patchRow(i, "role", e.target.value)}
                       className="bg-black/40 border-white/15"
                       data-testid={`staff-role-${i}`}/>
              </RowField>
              <RowField label="Phone">
                <Input value={c.phone || ""} placeholder="+91…"
                       onChange={(e) => patchRow(i, "phone", e.target.value)}
                       className="bg-black/40 border-white/15"
                       data-testid={`staff-phone-${i}`}/>
              </RowField>
              <RowField label="Email">
                <Input value={c.email || ""} placeholder="manager@stars.in"
                       onChange={(e) => patchRow(i, "email", e.target.value)}
                       className="bg-black/40 border-white/15"
                       data-testid={`staff-email-${i}`}/>
              </RowField>
              <Button variant="ghost" onClick={() => dropRow(i)}
                      className="text-white/50 hover:text-red-400"
                      data-testid={`staff-drop-${i}`}>
                <Trash2 size={14}/>
              </Button>
            </div>
          ))}
        </div>

        <Button onClick={saveContacts} disabled={saving}
                className="mt-5 btn-themed h-11 rounded-full uppercase tracking-[0.25em] text-xs"
                data-testid="save-staff-contacts-btn">
          Save contacts
        </Button>
      </div>

      {/* ─────────────────────── DM Whitelist ─────────────────────── */}
      <div className="mt-8 cs-card rounded-2xl p-6" data-testid="whitelist-panel">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={18} className="text-emerald-400"/>
          <div className="font-display text-xl">Direct-DM Whitelist</div>
        </div>
        <p className="text-xs text-white/55 mb-4">
          Anyone on this list can send you a DM directly even while
          the gate is up. Paste a user ID (from their profile URL) and
          press Add.
        </p>
        <div className="flex gap-2">
          <Input value={wlInput}
                 onChange={(e) => setWlInput(e.target.value)}
                 placeholder="user-uuid-here"
                 className="bg-black/40 border-white/15 font-mono text-xs"
                 data-testid="whitelist-input"/>
          <Button onClick={addWhitelist}
                  className="btn-themed rounded-full px-6 uppercase tracking-[0.2em] text-xs"
                  data-testid="whitelist-add-btn">
            <Plus size={14}/> Add
          </Button>
        </div>
        <div className="mt-4 space-y-2" data-testid="whitelist-list">
          {(data.dm_whitelist || []).length === 0 && (
            <div className="text-xs text-white/40">No whitelisted users.</div>
          )}
          {(data.dm_whitelist || []).map((uid) => (
            <div key={uid} className="flex items-center gap-3 border border-white/10 rounded-lg p-3"
                 data-testid={`whitelist-row-${uid}`}>
              <UserIcon size={14} className="text-white/55"/>
              <div className="flex-1 min-w-0 text-xs font-mono truncate">{uid}</div>
              <button onClick={() => removeWhitelist(uid)}
                      className="text-white/40 hover:text-red-400"
                      data-testid={`whitelist-drop-${uid}`}>
                <Trash2 size={14}/>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RowField({ label, children }) {
  return (
    <div>
      <Label className="text-[9px] tracking-[0.22em] uppercase text-white/50">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
